import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ParlingoState {
  games: any[];
  availableFixtures: any[];
  isLoading: boolean;
  isLoadingFixtures: boolean;
  error: string | null;
  previewData: any | null;
  // Pagination state
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  // Filter state
  currentStatus: string;
  difficultyOddsData: any | null;
  acceptedMarketData: any | null;
}

export const initialState: ParlingoState = {
  games: [],
  availableFixtures: [],
  isLoading: false,
  isLoadingFixtures: false,
  error: null,
  previewData: null,
  // Pagination defaults
  currentPage: 1,
  pageSize: 10,
  totalCount: 0,
  totalPages: 0,
  // Filter defaults
  currentStatus: 'all',
  difficultyOddsData: null,
  acceptedMarketData: null
};

const parlingoSlice = createSlice({
  name: 'parlingo',
  initialState,
  reducers: {
    // Get Parlingo games (defaults to draft status)
    getParlingoGames: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    // Get Parlingo games by status
    getParlingoGamesByStatus: (
      state,
      action: PayloadAction<{ status: string; pageNumber?: number; limit?: number }>
    ) => {
      state.isLoading = true;
      state.error = null;
    },
    getParlingoGamesSuccess: (
      state,
      action: PayloadAction<{
        games: any[];
        totalCount: number;
        pageNumber: number;
        limit: number;
        totalPages: number;
      }>
    ) => {
      state.games = action.payload.games;
      state.totalCount = action.payload.totalCount;
      state.currentPage = action.payload.pageNumber;
      state.pageSize = action.payload.limit;
      state.totalPages = action.payload.totalPages;
      state.isLoading = false;
      state.error = null;
    },
    getParlingoGamesFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Create Parlingo game
    createParlingoGame: (state, action: PayloadAction<any>) => {
      state.isLoading = true;
      state.error = null;
    },
    createParlingoGameSuccess: (state, action: PayloadAction<any>) => {
      state.games.unshift(action.payload); // Add new game to the beginning
      state.isLoading = false;
      state.error = null;
    },
    createParlingoGameFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Update Parlingo game status
    updateParlingoGameStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
      state.isLoading = true;
      state.error = null;
    },
    updateParlingoGameStatusSuccess: (state, action: PayloadAction<{ _id: string; status: string }>) => {
      const gameIndex = state.games.findIndex((game) => game._id === action.payload._id);
      if (gameIndex !== -1) {
        state.games[gameIndex].status = action.payload.status;
      }
      state.isLoading = false;
      state.error = null;
    },
    updateParlingoGameStatusFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Preview Parlingo game
    previewParlingoGame: (state, action: PayloadAction<any>) => {
      state.isLoading = true;
      state.error = null;
      state.previewData = null;
    },
    previewParlingoGameSuccess: (state, action: PayloadAction<any>) => {
      state.previewData = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    previewParlingoGameFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear preview data
    clearPreviewData: (state) => {
      state.previewData = null;
    },

    // Get available fixtures
    getAvailableFixtures: (state, action: PayloadAction<{ sport: string; leagues: string[] }>) => {
      state.isLoadingFixtures = true;
      state.error = null;
    },
    getAvailableFixturesSuccess: (state, action: PayloadAction<any[]>) => {
      state.availableFixtures = action.payload;
      state.isLoadingFixtures = false;
      state.error = null;
    },
    getAvailableFixturesFailure: (state, action: PayloadAction<string>) => {
      state.isLoadingFixtures = false;
      state.error = action.payload;
    },

    // Clear available fixtures
    clearAvailableFixtures: (state) => {
      state.availableFixtures = [];
    },

    // Set pagination params
    setPaginationParams: (state, action: PayloadAction<{ page: number; pageSize: number }>) => {
      state.currentPage = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },

    // Set status filter
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.currentStatus = action.payload;
      state.currentPage = 1; // Reset to first page when changing filter
    },

    // getParlingoDifficultyOddsRange
    getParlingoDifficultyOddsRange: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    getParlingoDifficultyOddsRangeSuccess: (state, action: PayloadAction<any>) => {
      state.difficultyOddsData = action.payload;
    },

    getParlingoDifficultyOddsRangeFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    //getAcceptedMarketDatalist
    getAcceptedMarketDatalist: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    getAcceptedMarketDatalistSuccess: (state, action: PayloadAction<any>) => {
      state.acceptedMarketData = action.payload;
    },

    getAcceptedMarketDatalistFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    }
  }
});

export const {
  getParlingoGames,
  getParlingoGamesByStatus,
  getParlingoGamesSuccess,
  getParlingoGamesFailure,
  createParlingoGame,
  createParlingoGameSuccess,
  createParlingoGameFailure,
  updateParlingoGameStatus,
  updateParlingoGameStatusSuccess,
  updateParlingoGameStatusFailure,
  previewParlingoGame,
  previewParlingoGameSuccess,
  previewParlingoGameFailure,
  getAvailableFixtures,
  getAvailableFixturesSuccess,
  getAvailableFixturesFailure,
  clearAvailableFixtures,
  clearError,
  clearPreviewData,
  setPaginationParams,
  setStatusFilter,
  getParlingoDifficultyOddsRange,
  getParlingoDifficultyOddsRangeSuccess,
  getParlingoDifficultyOddsRangeFailure,
  getAcceptedMarketDatalist,
  getAcceptedMarketDatalistSuccess,
  getAcceptedMarketDatalistFailure
} = parlingoSlice.actions;

export default parlingoSlice.reducer;
